import { Spinner, ErrorMessage, ModalInjectedProps } from '@sketch/components'
import { capitalize } from '@sketch/utils'

import React, { Suspense, useEffect, useState } from 'react'
import { FootnoteText, ModalBody } from './MoveStorageItemModal.styles'

import {
  MoveStorageItemModalFooter,
  MoveStorageItemModalLayout,
} from './MoveStorageItemModalLayout'
import {
  ProjectPickerContent,
  TreeStateProvider,
  TreeNodeTypes,
  useIsCreatingProject,
  useTreeState,
} from '../../../projects/modals/ProjectPickerContent'
import { useMoveStorageItems } from './useMoveStorageItems'
import {
  useGetProjectQuery,
  useGetWorkspaceQuery,
} from '@sketch/gql-types/src/generated/gqlTypes/apollo'
import {
  MoveStorageItemModalHeader,
  MoveStorageItemModalHeaderLoading,
  MoveStorageItemsModalHeader,
} from './MoveStorageItemModalHeader'
import { CurrentItemState } from '../../../projects/modals/ProjectPickerContent/state.base'
import { canAccessListChange } from './getLocation'
import { StorageItemIdentity } from '../../types'

const useCurrentTreeState = () => {
  const treeState = useTreeState()

  const selectedNode = treeState.selected
  const [, setSelected] = useState(treeState.selected)

  useEffect(() => {
    const handleUpdate = () => {
      setSelected(treeState.selected)
    }

    return treeState.registerUpdatesListener(handleUpdate)
  }, [treeState])

  const currentNode = treeState.currentDestination
  const currentIds = treeState.currentIds
  const current = treeState.current

  const canAccessChange = selectedNode?.payload
    ? canAccessListChange(selectedNode.payload, treeState)
    : false

  return {
    selectedNode,
    currentNode,
    currentIds,
    current,
    canAccessListChange: canAccessChange,
  }
}

interface MoveModalFooterProps {
  onClick: (data: TreeNodeTypes) => void
  loading: boolean
  actionLabel: string
  itemType: 'Document' | 'Folder' | 'Selection'
}

export const MoveModalFooter = (props: MoveModalFooterProps) => {
  const { onClick, loading, actionLabel } = props
  // WARNING: Use useCurrentTreeState subscribes to all changes in the tree state
  // and will re-render the component on every change and can lead to severely
  // degraded performance issues on large trees if not used correctly.
  //
  // To use it correctly - use it in the lowest possible node of React components tree
  // to avoid unnecessary re-renders of other components.
  const { selectedNode, current, currentIds, canAccessListChange } =
    useCurrentTreeState()

  const { isCreatingProject } = useIsCreatingProject()

  const payload = selectedNode?.payload
  const isEnabled =
    !!payload?.identifier &&
    (current.originType === 'stashed' ||
      payload?.identifier !== currentIds.currentId)

  return (
    <MoveStorageItemModalFooter
      actionLabel={actionLabel}
      indicateNextStep={canAccessListChange}
      disabled={!isEnabled}
      loading={loading}
      dimmed={isCreatingProject}
      leftContent={
        selectedNode?.payload &&
        (current.originType === 'stashed' ||
          selectedNode.payload.identifier !== currentIds.currentId) ? (
          <FootnoteText>
            {actionLabel} to <b>{selectedNode.payload.name}</b>
          </FootnoteText>
        ) : (
          <FootnoteText>
            Select a <b>folder…</b>
          </FootnoteText>
        )
      }
      onClick={() => {
        if (!payload) return
        onClick(payload)
      }}
    />
  )
}

interface MoveModalInnerProps {
  items: StorageItemIdentity[]
  onMove: () => void
  actionLabel: string
}

const MoveModalInner = (props: MoveModalInnerProps) => {
  const { items, onMove, actionLabel } = props
  const [moveStorageItem, { loading, ConfirmationModal }] = useMoveStorageItems(
    items,
    onMove
  )

  if (ConfirmationModal) {
    return <ConfirmationModal loading={loading} />
  }

  const [item] = items

  return (
    <MoveStorageItemModalLayout>
      {items.length > 1 ? (
        <MoveStorageItemsModalHeader items={items} actionLabel={actionLabel} />
      ) : (
        <MoveStorageItemModalHeader item={item} actionLabel={actionLabel} />
      )}

      <ProjectPickerContent />

      <MoveModalFooter
        itemType={
          items.length > 1
            ? 'Selection'
            : item.__typename === 'Share'
              ? 'Document'
              : 'Folder'
        }
        loading={loading}
        onClick={data => moveStorageItem(data)}
        actionLabel={actionLabel}
      />
    </MoveStorageItemModalLayout>
  )
}

export type MoveStorageItemModalAction = 'move' | 'restore' | 'unarchive'

export interface MoveStorageItemModalProps extends ModalInjectedProps {
  items: StorageItemIdentity[]
  workspaceIdentifier: string
  parentProjectIdentifier: string | null
  originType: 'stashed' | 'standard'
  action: MoveStorageItemModalAction
  onMove?: () => void
}

export const MoveStorageItemModal: React.FCC<
  MoveStorageItemModalProps
> = props => {
  const {
    items,
    workspaceIdentifier,
    parentProjectIdentifier,
    hideModal,
    originType = 'standard',
    action,
    onMove,
  }: typeof props = props

  // this query most likely will return a cached value
  const workspaceQuery = useGetWorkspaceQuery({
    variables: { identifier: workspaceIdentifier },
  })

  // this query most likely will return a cached value
  const parentProjectQuery = useGetProjectQuery({
    variables: { shortId: parentProjectIdentifier || '' },
    skip: !parentProjectIdentifier,
  })

  const [item] = items

  const loading = workspaceQuery.loading || parentProjectQuery.loading
  const error = workspaceQuery.error || parentProjectQuery.error

  const actionLabel = capitalize(action)

  const loadingLayout = (
    <MoveStorageItemModalLayout>
      <MoveStorageItemModalHeaderLoading actionLabel={actionLabel} />
      <ModalBody>
        <Spinner.Flex primary />
      </ModalBody>
      <MoveStorageItemModalFooter disabled loading actionLabel={actionLabel} />
    </MoveStorageItemModalLayout>
  )

  if (loading) {
    return loadingLayout
  }

  const workspace = workspaceQuery.data?.workspace
  const parentProject = parentProjectQuery.data?.project || null

  if (error || !workspace) {
    return (
      <MoveStorageItemModalLayout>
        <MoveStorageItemModalHeaderLoading actionLabel={actionLabel} />
        <ModalBody>
          <ErrorMessage.Generic />
        </ModalBody>
        <MoveStorageItemModalFooter disabled actionLabel={actionLabel} />
      </MoveStorageItemModalLayout>
    )
  }

  const treeStateContext: CurrentItemState = {
    item: item,
    workspace,
    project: parentProject,
    originType,
    disabledItems: items.map(i => i.identifier),
  }

  return (
    <Suspense fallback={loadingLayout}>
      <TreeStateProvider current={treeStateContext}>
        <MoveModalInner
          items={items}
          actionLabel={actionLabel}
          onMove={() => {
            hideModal()
            onMove?.()
          }}
        />
      </TreeStateProvider>
    </Suspense>
  )
}
